import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { pricesApi, productsApi, type Product, type ProductPrice } from '../crmApi'
import { apiErrorMessage } from '../api'
import { useDialogs } from '../components/Dialogs'

export function ProductEdit() {
  const { confirm } = useDialogs()
  const { id } = useParams()
  const isNew = id === undefined
  const productId = isNew ? undefined : Number(id)
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [availableFrom, setAvailableFrom] = useState('')
  const [availableUntil, setAvailableUntil] = useState('')
  const [initialPrice, setInitialPrice] = useState('')
  const [prices, setPrices] = useState<ProductPrice[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(isNew)

  // add-price form
  const [newValidFrom, setNewValidFrom] = useState('')
  const [newAmount, setNewAmount] = useState('')

  function fill(p: Product) {
    setName(p.name)
    setAvailableFrom(p.availableFrom)
    setAvailableUntil(p.availableUntil ?? '')
    setPrices(p.prices)
    setLoaded(true)
  }

  useEffect(() => {
    if (!isNew) productsApi.get(productId!).then(fill).catch((e) => setError(describe(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function saveProduct() {
    try {
      if (isNew) {
        if (!availableFrom) { setError('Ange tillgänglighetsdatum.'); return }
        const created = await productsApi.create({
          name, availableFrom, availableUntil: availableUntil || null, initialPrice: Number(initialPrice) || 0,
        })
        navigate(`/products/${created.id}`)
      } else {
        await productsApi.update(productId!, { name, availableFrom, availableUntil: availableUntil || null })
        const fresh = await productsApi.get(productId!)
        fill(fresh)
        setError(null)
      }
    } catch (e) { setError(describe(e)) }
  }

  async function removeProduct() {
    if (isNew) return
    if (!(await confirm('Radera produkten och alla dess priser?', { danger: true, confirmLabel: 'Radera' }))) return
    try { await productsApi.remove(productId!); navigate('/products') }
    catch (e) { setError(describe(e)) }
  }

  async function addPrice() {
    if (!newValidFrom) return
    try {
      await pricesApi.create(productId!, { validFrom: newValidFrom, amount: Number(newAmount) || 0 })
      setNewValidFrom(''); setNewAmount('')
      fill(await productsApi.get(productId!))
    } catch (e) { setError(describe(e)) }
  }

  async function removePrice(price: ProductPrice) {
    if (!(await confirm(`Radera priset från ${price.validFrom}?`, { danger: true, confirmLabel: 'Radera' }))) return
    try { await pricesApi.remove(productId!, price.id); fill(await productsApi.get(productId!)) }
    catch (e) { setError(describe(e)) }
  }

  if (!loaded) return <p className="muted">{error ?? 'Laddar…'}</p>

  return (
    <div>
      <div className="page-head">
        <h1>{isNew ? 'Ny produkt' : name}</h1>
        <button onClick={() => navigate('/products')}>Tillbaka</button>
      </div>
      {error && <p className="error">{error}</p>}

      <div className="card form-grid">
        <label className="field"><span className="field-label">Namn *</span>
          <input value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field"><span className="field-label">Tillgänglig fr.o.m. *</span>
          <input type="date" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} /></label>
        <label className="field"><span className="field-label">Tillgänglig t.o.m.</span>
          <input type="date" value={availableUntil} onChange={(e) => setAvailableUntil(e.target.value)} /></label>
        {isNew && (
          <label className="field"><span className="field-label">Initialt pris (inkl. moms) *</span>
            <input type="number" step="0.01" min="0" value={initialPrice} onChange={(e) => setInitialPrice(e.target.value)} /></label>
        )}
      </div>

      <div className="form-actions">
        <button className="primary" onClick={() => void saveProduct()}>Spara</button>
        {!isNew && <button onClick={() => void removeProduct()}>Radera</button>}
      </div>

      {!isNew && (
        <section className="card">
          <h2>Prislista (inkl. moms)</h2>
          <p className="muted">Priset som gäller ett visst datum är det med senaste startdatum som inte ligger efter datumet.</p>
          <div className="row-form">
            <input type="date" value={newValidFrom} onChange={(e) => setNewValidFrom(e.target.value)} />
            <input type="number" step="0.01" min="0" placeholder="Pris" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} />
            <button className="primary" onClick={() => void addPrice()}>Lägg till pris</button>
          </div>
          <table className="grid" style={{ marginTop: '0.8rem' }}>
            <thead><tr><th>Gäller fr.o.m.</th><th>Pris</th><th></th></tr></thead>
            <tbody>
              {prices.map((p) => (
                <tr key={p.id}>
                  <td>{p.validFrom}</td>
                  <td>{p.amount.toFixed(2)} kr</td>
                  <td className="actions"><button onClick={() => void removePrice(p)}>Radera</button></td>
                </tr>
              ))}
              {prices.length === 0 && <tr><td colSpan={3} className="muted">Inga priser.</td></tr>}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}

const describe = apiErrorMessage
